import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
export const GlobalLoadingStateContext = /*#__PURE__*/createContext({
  globalLoadingStates: {},
  addGlobalLoadingStateListeners: () => {},
  notifyGlobalLoadingStatesChange: () => {}
});

/*

  This is a global context that is used to manage the global loading state of the application.
  Each API call will have a GlobalLoadingStateName that will initiate as undefined.
  Once the first useQuery is called, the loading state will be set to true.
  Once the data is fetched, the loading state will be set to false.
  It is never set again afterwards.

  This is a pattern that is used to determine whether the application is ready.
  This is not to be used in the place of the local loading state returned by the useQuery hook.
  Components should listen to that local loading state, to determine if the data needed to render the component is ready.

  This will eventually replace the existing loading state selectors, in particular hasPricingAndPurchasingSucceededSelector
  The pattern used in useGlobalLoading can be used to migrate iteratively.

  As a follow-up to the MTV MVP, we can analyze the usages of the loading state selectors and reduce the amount of API loading state dependencies
  for specific use cases.

  Currently hasPricingAndPurchasingSucceededSelector is serving 2 purposes:
    1. Telling components if the data is ready to render
    2. Safeguarding the seletors & utils when essential pieces of data are not ready

  Ideally:
    1. Components should be concerned with the local loading states. This is particularly true for components that have data which changes.
      For example: business objectives & features which change when the user switches tabs
    2. Selectors and utils should either be refactored to handle undefined gracefully, or only care about the data needed within them,
      which is not necessarily each of the primary API calls that currently hydrate hasPricingAndPurchasingSucceededSelector

*/

export const GlobalLoadingStateContextProvider = ({
  children
}) => {
  const globalLoadingStatesRef = useRef({});
  const globalLoadingStateListeners = useRef({
    PurchaseOptions: new Set(),
    BusinessObjectives: new Set(),
    Currencies: new Set()
  });
  const addGlobalLoadingStateListeners = useCallback((names, listener) => {
    const listeners = globalLoadingStateListeners.current;
    for (const name of names) {
      var _listeners$name;
      (_listeners$name = listeners[name]) === null || _listeners$name === void 0 || _listeners$name.add(listener);
    }
    return () => {
      for (const name of names) {
        var _listeners$name2;
        (_listeners$name2 = listeners[name]) === null || _listeners$name2 === void 0 || _listeners$name2.delete(listener);
      }
    };
  }, []);
  const notifyGlobalLoadingStatesChange = useCallback(globalLoadingStates => {
    const listeners = new Map();
    for (const {
      name,
      loadingCallback
    } of globalLoadingStates) {
      const currentLoadingValue = globalLoadingStatesRef.current[name];
      const loadingValueFromCallback = loadingCallback(currentLoadingValue);
      if (loadingValueFromCallback !== globalLoadingStatesRef.current[name]) {
        globalLoadingStatesRef.current[name] = loadingValueFromCallback;
        for (const listener of globalLoadingStateListeners.current[name]) {
          var _listeners$get;
          if (!listeners.has(listener)) {
            listeners.set(listener, []);
          }
          (_listeners$get = listeners.get(listener)) === null || _listeners$get === void 0 || _listeners$get.push({
            name,
            loadingCallback
          });
        }
      }
    }
    for (const [listener, listenerLoadingStates] of listeners) {
      listener(listenerLoadingStates);
    }
    listeners.clear();
  }, []);
  return /*#__PURE__*/_jsx(GlobalLoadingStateContext.Provider, {
    value: {
      globalLoadingStates: globalLoadingStatesRef.current,
      addGlobalLoadingStateListeners,
      notifyGlobalLoadingStatesChange
    },
    children: children
  });
};
const useLoadingStates = names => {
  const {
    globalLoadingStates: allLoadingStates,
    addGlobalLoadingStateListeners
  } = useContext(GlobalLoadingStateContext);
  const [globalLoadingStates, setLoadingStates] = useState(() => {
    const initLoadingStates = {};
    for (const name of names) {
      initLoadingStates[name] = allLoadingStates[name];
    }
    return initLoadingStates;
  });
  useLayoutEffect(() => {
    return addGlobalLoadingStateListeners(names, stateUpdates => {
      setLoadingStates(prevLoadingStates => {
        const newLoadingStates = Object.assign({}, prevLoadingStates);
        for (const {
          name,
          loadingCallback
        } of stateUpdates) {
          newLoadingStates[name] = loadingCallback();
        }
        return newLoadingStates;
      });
    });
  }, [addGlobalLoadingStateListeners, names]);
  return globalLoadingStates;
};
export const useGlobalLoadingState = name => {
  const currentLoadingState = useLoadingStates([name]);
  return currentLoadingState[name];
};
const useSetLoadingStates = () => {
  const {
    notifyGlobalLoadingStatesChange
  } = useContext(GlobalLoadingStateContext);
  return notifyGlobalLoadingStatesChange;
};
const useSetLoadingState = () => {
  return useSetLoadingStates();
};
export const useSetOneGlobalLoadingState = (name, onCompleted) => {
  const setLoadingState = useSetLoadingState();
  setLoadingState([{
    name,
    loadingCallback: prevLoading => {
      return prevLoading === undefined ? true : prevLoading;
    }
  }]);
  const onCompletedCallback = () => {
    if (onCompleted) {
      onCompleted();
    }
    setLoadingState([{
      name,
      loadingCallback: () => false
    }]);
  };
  return onCompletedCallback;
};